/**
 * Lightweight motion helpers for nav / route feel.
 * Goal: noticed but not theatrical — ~350–450ms, directional when tabs move.
 */

/** Primary tab strip order (matches BottomNav left → right). */
const TAB_ROOTS = ['/doctors', '/clinics', '/', '/student', '/blogs', '/events', '/student/profile', '/login', '/plans'] as const;

function rootOf(path: string): string {
  if (path === '/') return '/';
  if (path.startsWith('/clinics')) return '/clinics';
  if (path.startsWith('/doctors')) return '/doctors';
  if (path.startsWith('/blogs')) return '/blogs';
  if (path.startsWith('/events')) return '/events';
  if (path.startsWith('/student/profile')) return '/student/profile';
  if (path.startsWith('/student')) return '/student';
  if (path.startsWith('/plans')) return '/plans';
  if (path.startsWith('/login') || path.startsWith('/register')) return '/login';
  return path;
}

export type NavDirection = 'forward' | 'back' | 'fade';

export function navDirection(fromPath: string, toPath: string): NavDirection {
  const from = rootOf(fromPath);
  const to = rootOf(toPath);
  if (from === to) return 'fade';
  const a = TAB_ROOTS.indexOf(from as (typeof TAB_ROOTS)[number]);
  const b = TAB_ROOTS.indexOf(to as (typeof TAB_ROOTS)[number]);
  if (a < 0 || b < 0) return 'fade';
  return b > a ? 'forward' : 'back';
}

export function setNavDirection(dir: NavDirection) {
  document.documentElement.dataset.navDir = dir;
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function supportsViewTransitions(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document;
}

/** Soft ease used across pill + page motion. */
export const MOTION_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** Pill slide — slow enough to read, fast enough for mobile tabs. */
export const PILL_MS = 360;

/** Lead time so the pill starts before the page swaps. */
export const NAV_LEAD_MS = 90;

/** Page / view-transition duration. */
export const PAGE_MS = 420;
